import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { PayrollCalculationService } from './payroll-calculation.service'
import { forkJoin, map, Observable } from 'rxjs'
import {
  HistoricalPayroll,
  PayrollCalculationBreakdown,
  PayrollCalculationContext,
  PayrollSummary,
  PayrollSummaryTableData,
  SummaryTableRow
} from '../store/payroll/payroll.state'
import { EmployeeSalary } from '../store/employee-salary/employee-salary.state'
import { eps } from './url.service'
import dayjs from 'dayjs'

@Injectable({ providedIn: 'root' })
export class PayrollSummaryService {
  private http = inject(HttpClient)
  private payrollApi = eps.hrm.runPayrollApi
  private salaryApi = eps.hrm.createSalaryApi
  private payrollCalcService = inject(PayrollCalculationService)
  private readonly MONTH_NAMES = [
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
    'Jan',
    'Feb',
    'Mar'
  ]

  /**
   * Main public method to be called by the NgRx effect.
   * It orchestrates fetching, calculating, and transforming payroll data for a full financial year.
   */
  //kripesh
  // public getPayrollSummary(
  //   employeeId: string,
  //   financialYear: string,
  //   joiningDate: Date
  // ): Observable<PayrollSummary> {
  //   //const { startDate, endDate } = this.getFinancialYearDates(financialYear)
  //   //const today = new Date()

  //   // Fetch historical data and all salary revisions concurrently
  //   const historicalData$ = this.fetchHistoricalPayrolls(employeeId, financialYear)
  //   const salaries$ = this.fetchEmployeeSalaries(employeeId)

  //   return forkJoin({ historicalData: historicalData$, salaries: salaries$ }).pipe(
  //     map(({ historicalData, salaries }) => {
  //       if (salaries.length === 0) {
  //         return this.createEmptySummary(financialYear)
  //       }
  //       // const allMonths = this.generateMonthsForFinancialYear(financialYear)
  //       const joinDateObj = new Date(joiningDate) // make sure it's a Date
  //       const joinMonthStart = new Date(joinDateObj.getFullYear(), joinDateObj.getMonth(), 1)

  //       const allMonths = this.generateMonthsForFinancialYear(financialYear).filter(m => {
  //         const [year, month] = m.yearMonth.split('-').map(Number)
  //         const monthStart = new Date(year, month - 1, 1)
  //         return monthStart >= joinMonthStart
  //       })

  //       const processedMonthsSet = new Set(historicalData.map(h => h.month))
  //       const monthsWithHistory = allMonths.filter(m => processedMonthsSet.has(m.yearMonth))
  //       const monthsToProject = allMonths.filter(m => !processedMonthsSet.has(m.yearMonth))
  //       // console.log('monthsToProject', monthsToProject)
  //       // const monthsToProject = allMonths.filter(m => {
  //       //   const [year, month] = m.yearMonth.split('-').map(Number)
  //       //   const monthStart = new Date(year, month - 1, 1)
  //       //   return !processedMonthsSet.has(m.yearMonth) && monthStart >= new Date(joiningDate)
  //       // })

  //       const pastMonthsData = this.processPastMonths(monthsWithHistory, historicalData)
  //       const futureMonthsData = this.projectFutureMonths(monthsToProject, salaries)

  //       // 3. Transform the collected monthly breakdowns into the table format for the UI
  //       const pastTable = this.transformBreakdownsToTable(pastMonthsData)
  //       const futureTable = this.transformBreakdownsToTable(futureMonthsData)
  //       console.log('past table', pastTable)
  //       return {
  //         past: pastTable,
  //         future: futureTable
  //       }
  //     })
  //   )
  // }

  //gps 7 january 2026
  public getPayrollSummary(
    employeeId: string,
    financialYear: string,
    joiningDate: Date
  ): Observable<PayrollSummary> {
    // Fetch historical data and employee salaries concurrently
    const historicalData$ = this.fetchHistoricalPayrolls(employeeId, financialYear)
    const salaries$ = this.fetchEmployeeSalaries(employeeId)

    return forkJoin({ historicalData: historicalData$, salaries: salaries$ }).pipe(
      map(({ historicalData, salaries }) => {
        // If no salaries, return empty summary
        if (salaries.length === 0) {
          return this.createEmptySummary(financialYear)
        }

        // 1️⃣ Generate all months for the financial year
        const allMonths = this.generateMonthsForFinancialYear(financialYear)

        // 2️⃣ Filter out months before joining date
        const joinDateObj = new Date(joiningDate)
        const joinMonthStart = new Date(joinDateObj.getFullYear(), joinDateObj.getMonth(), 1)
        const eligibleMonths = allMonths.filter(m => {
          const [year, month] = m.yearMonth.split('-').map(Number)
          const monthStart = new Date(year, month - 1, 1)
          return monthStart >= joinMonthStart
        })

        // 3️⃣ Split historical vs future months
        const processedMonthsSet = new Set(historicalData.map(h => h.month))
        const monthsWithHistory = eligibleMonths.filter(m => processedMonthsSet.has(m.yearMonth))

        // 4️⃣ Determine last month of history
        let lastPastMonth: string | null = null
        if (historicalData.length > 0) {
          lastPastMonth = historicalData
            .map(h => h.month)
            .sort() // YYYY-MM strings sort correctly
            .pop()!
        }

        // 5️⃣ Only project months after last historical month
        const monthsToProject = eligibleMonths.filter(
          m =>
            !processedMonthsSet.has(m.yearMonth) && (!lastPastMonth || m.yearMonth > lastPastMonth)
        )

        // 6️⃣ Process past and future months
        const pastMonthsData = this.processPastMonths(monthsWithHistory, historicalData)
        const futureMonthsData = this.projectFutureMonths(monthsToProject, salaries)

        // 7️⃣ Transform for UI table
        const pastTable = this.transformBreakdownsToTable(pastMonthsData)
        const futureTable = this.transformBreakdownsToTable(futureMonthsData)

        return {
          past: pastTable,
          future: futureTable
        }
      })
    )
  }

  // =================================================================
  // PRIVATE HELPER METHODS
  // =================================================================

  private fetchHistoricalPayrolls(
    employeeId: string,
    financialYear: string
  ): Observable<HistoricalPayroll[]> {
    return this.http.get<HistoricalPayroll[]>(`${this.payrollApi}/${employeeId}/payroll-history`, {
      params: { financialYear }
    })
  }

  private fetchEmployeeSalaries(employeeId: string): Observable<EmployeeSalary[]> {
    return this.http.get<EmployeeSalary[]>(`${this.salaryApi}/all`, {
      params: { employeeId }
    })
  }

  // private processPastMonths(
  //   monthsWithHistory: { yearMonth: string; monthName: string }[],
  //   historicalData: HistoricalPayroll[]
  // ): { monthName: string; breakdown: PayrollCalculationBreakdown }[] {
  //   const historicalMap = new Map(historicalData.map(h => [h.month, h.calculationBreakdown]))

  //   return monthsWithHistory.map(({ yearMonth, monthName }) => ({
  //     monthName: dayjs(yearMonth).format('MMM YYYY'),
  //     breakdown: historicalMap.get(yearMonth) || { earnings: [], deductions: [] }
  //   }))
  // }

  //gps
  private processPastMonths(
    monthsWithHistory: { yearMonth: string; monthName: string }[],
    historicalData: HistoricalPayroll[]
  ): {
    monthName: string
    breakdown: PayrollCalculationBreakdown
    tdsAfterCess: number
    lop: number
  }[] {
    const historicalMap = new Map(
      historicalData.map(h => [
        h.month,
        {
          breakdown: h.calculationBreakdown,
          tdsAfterCess: h.tdsAfterCess,
          lop: h.lop
        }
      ])
    )

    return monthsWithHistory.map(({ yearMonth }) => {
      const data = historicalMap.get(yearMonth)
      return {
        monthName: dayjs(yearMonth).format('MMM YYYY'),
        breakdown: data?.breakdown || { earnings: [], deductions: [] },
        tdsAfterCess: data?.tdsAfterCess ?? 0,
        lop: data?.lop ?? 0
      }
    })
  }

  /**
   * Calculates the estimated breakdown for all months provided.
   */
  // private projectFutureMonths(
  //   monthsToProject: { yearMonth: string; monthName: string }[],
  //   salaries: EmployeeSalary[] // Assumed to be sorted by effectiveFrom
  // ): { monthName: string; breakdown: PayrollCalculationBreakdown }[] {
  //   return monthsToProject.map(({ yearMonth, monthName }) => {
  //     // For each future month, find the correct salary records to use
  //     const { currentSalary, previousSalary } = this.findSalariesForMonth(yearMonth, salaries)

  //     if (!currentSalary) {
  //       return { monthName, breakdown: { earnings: [], deductions: [] } }
  //     }

  //     const payPeriodStart = `${yearMonth}-01`
  //     const daysInMonth = new Date(
  //       parseInt(yearMonth.substring(0, 4)),
  //       parseInt(yearMonth.substring(5, 7)),
  //       0
  //     ).getDate()
  //     const payPeriodEnd = `${yearMonth}-${daysInMonth}`

  //     // Create the context with 0 for all optional/one-off values
  //     const context: PayrollCalculationContext = {
  //       currentSalary,
  //       previousSalary,
  //       payPeriodStart,
  //       payPeriodEnd,
  //       lopDays: 0,
  //       lopDaysForArrearsPeriod: 0,
  //       bonus: 0,
  //       expenses: 0,
  //       adjustments: 0,
  //       rounding: 0
  //     }

  //     const result = this.payrollCalcService.calculatePayrollMetrics(context)
  //     return {
  //       monthName: dayjs(yearMonth).format('MMM YYYY'),
  //       breakdown: result.calculationBreakdown || { earnings: [], deductions: [] }
  //     }
  //   })
  // }

  //gps
  private projectFutureMonths(
    monthsToProject: { yearMonth: string; monthName: string }[],
    salaries: EmployeeSalary[] // Assumed to be sorted by effectiveFrom
  ): {
    monthName: string
    breakdown: PayrollCalculationBreakdown
    tdsAfterCess: number
    lop: number
  }[] {
    return monthsToProject.map(({ yearMonth, monthName }) => {
      // For each future month, find the correct salary records to use
      const { currentSalary, previousSalary } = this.findSalariesForMonth(yearMonth, salaries)

      if (!currentSalary) {
        return { monthName, breakdown: { earnings: [], deductions: [] }, tdsAfterCess: 0, lop: 0 }
      }

      const payPeriodStart = `${yearMonth}-01`
      const daysInMonth = new Date(
        parseInt(yearMonth.substring(0, 4)),
        parseInt(yearMonth.substring(5, 7)),
        0
      ).getDate()
      const payPeriodEnd = `${yearMonth}-${daysInMonth}`

      // Create the context with 0 for all optional/one-off values
      const context: PayrollCalculationContext = {
        currentSalary,
        previousSalary,
        payPeriodStart,
        payPeriodEnd,
        lopDays: 0,
        lopDaysForArrearsPeriod: 0,
        bonus: 0,
        expenses: 0,
        adjustments: 0,
        rounding: 0
      }

      const result = this.payrollCalcService.calculatePayrollMetrics(context)

      return {
        monthName: dayjs(yearMonth).format('MMM YYYY'),
        breakdown: result.calculationBreakdown || { earnings: [], deductions: [] },
        tdsAfterCess: result.tdsAfterCess || 0,
        lop: 0
      }
    })
  }

  /**
   * This is a key logic piece. For a given month, it finds the salary that should be
   * considered "current" and the one that is "previous".
   */
  private findSalariesForMonth(
    yearMonth: string, // "YYYY-MM"
    salaries: EmployeeSalary[]
  ): { currentSalary: EmployeeSalary | null; previousSalary: EmployeeSalary | null } {
    const monthStart = new Date(yearMonth + '-01T00:00:00Z')

    // Find the latest salary that is effective on or before the start of the target month.
    let currentSalary: EmployeeSalary | null = null
    for (const salary of salaries) {
      const effectiveFrom = new Date(salary.effectiveFrom + 'T00:00:00Z')
      if (effectiveFrom <= monthStart) {
        currentSalary = salary
      } else {
        // Since salaries are sorted, we can stop once we pass the target month.
        break
      }
    }

    if (!currentSalary) return { currentSalary: null, previousSalary: null }

    // The previous salary is the one right before the current one in the sorted list.
    const currentIndex = salaries.findIndex(s => s.effectiveFrom === currentSalary!.effectiveFrom)
    const previousSalary = currentIndex > 0 ? salaries[currentIndex - 1] : null

    // Special check for proration/arrears: If the 'payoutFrom' of the *next* salary
    // falls in our target month, that *next* salary becomes our 'current' one for the calculation.
    const nextSalary = salaries[currentIndex + 1]
    if (nextSalary && nextSalary.payoutFrom === yearMonth) {
      return { currentSalary: nextSalary, previousSalary: currentSalary }
    }

    return { currentSalary, previousSalary }
  }

  /**
   * The core transformation function. Takes an array of monthly breakdowns
   * and pivots it into the table structure required by the UI.
   */
  // private transformBreakdownsToTable(
  //   monthlyData: { monthName: string; breakdown: PayrollCalculationBreakdown }[]
  // ): PayrollSummaryTableData {
  //   if (monthlyData.length === 0) {
  //     return {
  //       header: ['Total'],
  //       earningsRows: [],
  //       deductionsRows: [],
  //       totals: this.createEmptyTotalsRow()
  //     }
  //   }

  //   const header = [...monthlyData.map(md => md.monthName), 'Total']
  //   const earningsMap = new Map<string, number[]>()
  //   const deductionsMap = new Map<string, number[]>()

  //   // Initialize maps with all component names to ensure all rows have the same length
  //   const allEarningNames = new Set<string>()
  //   const allDeductionNames = new Set<string>()
  //   monthlyData.forEach(({ breakdown }) => {
  //     breakdown.earnings.forEach(c => allEarningNames.add(c.name))
  //     breakdown.deductions.forEach(c => allDeductionNames.add(c.name))
  //   })

  //   allEarningNames.forEach(name => earningsMap.set(name, Array(monthlyData.length).fill(0)))
  //   allDeductionNames.forEach(name => deductionsMap.set(name, Array(monthlyData.length).fill(0)))

  //   // Populate the maps with data
  //   monthlyData.forEach(({ breakdown }, monthIndex) => {
  //     breakdown.earnings.forEach(comp => {
  //       earningsMap.get(comp.name)![monthIndex] = comp.amount
  //     })
  //     breakdown.deductions.forEach(comp => {
  //       deductionsMap.get(comp.name)![monthIndex] = comp.amount
  //     })
  //   })

  //   const toSummaryRow = (name: string, values: number[]): SummaryTableRow => ({
  //     componentName: name,
  //     values: [...values, values.reduce((sum, v) => sum + v, 0)] // Add total
  //   })

  //   const earningsRows = Array.from(earningsMap.entries()).map(([name, values]) =>
  //     toSummaryRow(name, values)
  //   )
  //   const deductionsRows = Array.from(deductionsMap.entries()).map(([name, values]) =>
  //     toSummaryRow(name, values)
  //   )

  //   // Calculate Totals rows (Gross, Total Deductions, Net)
  //   const grossTotals = Array(monthlyData.length).fill(0)
  //   const deductionTotals = Array(monthlyData.length).fill(0)
  //   monthlyData.forEach((data, index) => {
  //     grossTotals[index] = data.breakdown.earnings.reduce((sum, c) => sum + c.amount, 0)
  //     deductionTotals[index] = data.breakdown.deductions.reduce((sum, c) => sum + c.amount, 0)
  //   })

  //   const netTotals = grossTotals.map((g, i) => g - deductionTotals[i])

  //   const totals = {
  //     gross: toSummaryRow('Total Gross Earnings', grossTotals),
  //     deductions: toSummaryRow('Total Deductions', deductionTotals),
  //     net: toSummaryRow('Net Salary', netTotals)
  //   }

  //   return { header, earningsRows, deductionsRows, totals }
  // }

  //gps
  private transformBreakdownsToTable(
    monthlyData: {
      monthName: string
      breakdown: PayrollCalculationBreakdown
      tdsAfterCess: number | string
      lop: number | string
    }[]
  ): PayrollSummaryTableData & { tdsRows: SummaryTableRow[]; lopRows: SummaryTableRow[] } {
    const n = (x: unknown) =>
      x === null || x === undefined || x === ''
        ? 0
        : typeof x === 'number'
          ? x
          : parseFloat(String(x))

    if (monthlyData.length === 0) {
      return {
        header: ['Total'],
        earningsRows: [],
        deductionsRows: [],
        tdsRows: [],
        lopRows: [],
        totals: this.createEmptyTotalsRow()
      }
    }

    const header = [...monthlyData.map(md => md.monthName), 'Total']
    const earningsMap = new Map<string, number[]>()
    const deductionsMap = new Map<string, number[]>()

    const allEarningNames = new Set<string>()
    const allDeductionNames = new Set<string>()
    monthlyData.forEach(({ breakdown }) => {
      breakdown.earnings.forEach(c => allEarningNames.add(c.name))
      breakdown.deductions.forEach(c => allDeductionNames.add(c.name))
    })

    allEarningNames.forEach(name => earningsMap.set(name, Array(monthlyData.length).fill(0)))
    allDeductionNames.forEach(name => deductionsMap.set(name, Array(monthlyData.length).fill(0)))

    monthlyData.forEach(({ breakdown }, monthIndex) => {
      breakdown.earnings.forEach(comp => {
        earningsMap.get(comp.name)![monthIndex] = n(comp.amount)
      })
      breakdown.deductions.forEach(comp => {
        deductionsMap.get(comp.name)![monthIndex] = n(comp.amount)
      })
    })

    const toSummaryRow = (name: string, rawValues: Array<number | string>): SummaryTableRow => {
      const values = rawValues.map(v => n(v))
      const total = values.reduce((sum, v) => sum + v, 0)
      return { componentName: name, values: [...values, total] }
    }

    const earningsRows = Array.from(earningsMap.entries()).map(([name, values]) =>
      toSummaryRow(name, values)
    )
    const deductionsRows = Array.from(deductionsMap.entries()).map(([name, values]) =>
      toSummaryRow(name, values)
    )

    // Separate rows for TDS and LOP (cast to number!)
    const tdsRows = [
      toSummaryRow(
        'TDS After Cess',
        monthlyData.map(md => md.tdsAfterCess)
      )
    ]
    const lopRows = [
      toSummaryRow(
        'Loss of Pay (Days)',
        monthlyData.map(md => md.lop)
      )
    ]

    const grossTotals = Array(monthlyData.length).fill(0)
    const deductionTotals = Array(monthlyData.length).fill(0)
    monthlyData.forEach((data, index) => {
      grossTotals[index] = data.breakdown.earnings.reduce((s, c) => s + n(c.amount), 0)
      deductionTotals[index] =
        data.breakdown.deductions.reduce((s, c) => s + n(c.amount), 0) + n(data.tdsAfterCess)
      // +n(data.lop)
    })

    const netTotals = grossTotals.map((g, i) => g - deductionTotals[i])

    const totals = {
      gross: toSummaryRow('Total Gross Earnings', grossTotals),
      deductions: toSummaryRow('Total Deductions', deductionTotals),
      net: toSummaryRow('Net Salary', netTotals)
    }

    return { header, earningsRows, deductionsRows, tdsRows, lopRows, totals }
  }

  // Utility to create empty totals rows
  private createEmptyTotalsRow() {
    return {
      gross: { componentName: 'Total Gross Earnings', values: [0] },
      deductions: { componentName: 'Total Deductions', values: [0] },
      net: { componentName: 'Net Salary', values: [0] }
    }
  }

  // Utility to create a fully empty summary object
  private createEmptySummary(financialYear: string): PayrollSummary {
    const allMonths = this.generateMonthsForFinancialYear(financialYear)
    const emptyTableData: PayrollSummaryTableData = {
      header: [...allMonths.map(m => m.monthName), 'Total'],
      earningsRows: [],
      deductionsRows: [],
      totals: this.createEmptyTotalsRow()
    }
    return { past: emptyTableData, future: emptyTableData }
  }

  /**
   * Generates a list of months for the given financial year string (e.g., "2024-2025").
   */
  private generateMonthsForFinancialYear(
    financialYear: string
  ): { yearMonth: string; monthName: string }[] {
    const startYear = parseInt(financialYear.split('-')[0])
    const months = []
    for (let i = 0; i < 12; i++) {
      const month = (i + 3) % 12 // April is month 3 (0-indexed)
      const year = month < 3 ? startYear + 1 : startYear
      const monthStr = (month + 1).toString().padStart(2, '0')
      months.push({
        yearMonth: `${year}-${monthStr}`,
        monthName: this.MONTH_NAMES[i]
      })
    }
    return months
  }

  //  private getFinancialYearDates(financialYear: string): { startDate: Date; endDate: Date } {
  //    const startYear = parseInt(financialYear.split('-')[0])
  //    const startDate = new Date(`${startYear}-04-01T00:00:00Z`)
  //    const endDate = new Date(`${startYear + 1}-03-31T00:00:00Z`)
  //    return { startDate, endDate }
  //  }

  getHistoricalPayrollFinancialYearForEmployee(employeeId: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.payrollApi}/${employeeId}/payroll-history/financialYear`)
  }
}
